import { useRef, useState } from "react";
import { MAIN_SITE, track } from "@/lib/blog";
import { SITE_PHONE_DISPLAY } from "@/lib/site";

const GROUPS: { legend: string; items: { label: string; question: string }[] }[] = [
  {
    legend: "Layout",
    items: [
      { label: "More room to move", question: "Which door swings, fixture locations, or clearances should we review?" },
      { label: "A better tub or shower setup", question: "What bathing arrangement fits our routine and the available space?" },
    ],
  },
  {
    legend: "Storage",
    items: [
      { label: "Less countertop clutter", question: "Which everyday items need a dedicated storage place?" },
      { label: "More space for towels and toiletries", question: "What storage could fit without crowding the room?" },
    ],
  },
  {
    legend: "Finishes",
    items: [
      { label: "Surfaces that are easier to care for", question: "What cleaning and maintenance does each proposed material need?" },
      { label: "Updated colors, fixtures, and finishes", question: "Which selections work together and suit their intended use?" },
    ],
  },
  {
    legend: "Everyday use",
    items: [
      { label: "Better lighting and ventilation", question: "What should be assessed about the room’s lighting and exhaust ventilation?" },
      { label: "Features that support changing needs", question: "Which access, reach, or support needs should we discuss before finalizing the layout?" },
    ],
  },
];
const ALL = GROUPS.flatMap((g) => g.items);

export function PriorityChecklist({ title, url, slug }: { title: string; url: string; slug: string }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [copyMsg, setCopyMsg] = useState("");
  const used = useRef(false);
  const picks = ALL.filter((i) => selected.includes(i.label));
  const n = picks.length;

  function toggle(label: string) {
    if (!used.current) {
      used.current = true;
      track("priority_checklist_use", { article_slug: slug, interaction_type: "select" });
    }
    setCopyMsg("");
    setSelected((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]));
  }

  async function copy() {
    const text = [title, url, "", ...picks.flatMap((p) => [`- ${p.label}`, `  ${p.question}`])].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg("Priorities copied.");
    } catch {
      setCopyMsg("Copy didn’t work here — you can select the notes below and copy them manually.");
    }
  }

  function print() {
    document.body.classList.add("printing-priorities");
    const done = () => {
      document.body.classList.remove("printing-priorities");
      window.removeEventListener("afterprint", done);
    };
    window.addEventListener("afterprint", done);
    window.print();
  }

  const btn =
    "inline-flex min-h-11 items-center justify-center rounded-md px-4 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className="not-prose my-10 rounded-2xl border border-border bg-cream p-6 sm:p-8">
      <p className="text-base text-muted-foreground">Choose the changes that matter most to you.</p>
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {GROUPS.map((g) => (
          <fieldset key={g.legend} className="rounded-xl bg-background p-5">
            <legend className="px-1 font-display text-lg text-navy">{g.legend}</legend>
            <div className="mt-2 grid gap-2">
              {g.items.map((i) => (
                <label key={i.label} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-muted">
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-[var(--primary-ink)]"
                    checked={selected.includes(i.label)}
                    onChange={() => toggle(i.label)}
                  />
                  <span className="text-base text-foreground">{i.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <p role="status" aria-live="polite" className="mt-6 font-semibold text-navy">
        {n} {n === 1 ? "priority" : "priorities"} selected.
      </p>

      {n > 0 && (
        <div className="mt-4 rounded-xl bg-background p-5">
          <h3 className="font-display text-xl text-navy">Your planning notes</h3>
          <ul className="mt-3 grid gap-3">
            {picks.map((p) => (
              <li key={p.label}>
                <strong className="text-navy">{p.label}</strong>
                <div className="text-muted-foreground">{p.question}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" disabled={n === 0} onClick={copy} className={`${btn} bg-navy text-navy-foreground`}>
          Copy My Priorities
        </button>
        <button type="button" disabled={n === 0} onClick={print} className={`${btn} border border-navy text-navy`}>
          Print My Priorities
        </button>
        <button
          type="button"
          onClick={() => { setSelected([]); setCopyMsg(""); }}
          className={`${btn} text-navy underline`}
        >
          Clear Selections
        </button>
        <a
          href={`${MAIN_SITE}/contact-us`}
          onClick={() => track("article_contact_click", { article_slug: slug, interaction_type: "checklist_cta" })}
          className={`${btn} bg-gradient-primary text-navy`}
        >
          Discuss Your Bathroom Project
        </a>
      </div>
      {copyMsg && <p className="mt-3 text-sm text-navy">{copyMsg}</p>}

      <div className="print-sheet hidden">
        <p><strong>{title}</strong></p>
        <ul>
          {picks.map((p) => (
            <li key={p.label}>
              <strong>{p.label}</strong> — {p.question}
            </li>
          ))}
        </ul>
        <p>{url}</p>
        <p>Dog Day Renovations · {SITE_PHONE_DISPLAY}</p>
      </div>
    </div>
  );
}
